import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus, Trash2, Server, ShieldCheck, ShieldOff, CheckCircle2,
  AlertCircle, X, Pencil,
} from 'lucide-react';
import api from '../lib/api';
import MissionShell from '../components/MissionShell';
import { MISSIONS } from '../lib/missions';

const EMPTY = {
  name: '', provider: '', kind: 'saas', hosting_country: '',
  has_logs: false, dpa_signed: false, dpa_date: '',
  internal_owner: '', contact_email: '', notes: '',
};

export default function Sistemas() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ kinds: {} });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [list, m] = await Promise.all([api.get('/systems'), api.get('/systems/meta')]);
      setItems(list.data); setMeta(m.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (payload, id) => {
    try {
      if (id) await api.put(`/systems/${id}`, payload);
      else await api.post('/systems', payload);
      toast.success(id ? 'Sistema actualizado' : 'Sistema registrado');
      setEditing(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const del = async (id) => {
    if (!window.confirm('¿Eliminar este sistema del catastro?')) return;
    await api.delete(`/systems/${id}`);
    toast.success('Sistema eliminado');
    await load();
  };

  if (loading) return <div className="text-sm text-[#6B7280]">Cargando catastro…</div>;

  const withoutDpa = items.filter((s) => !s.dpa_signed).length;
  const withoutLogs = items.filter((s) => !s.has_logs).length;

  return (
    <MissionShell
      mission={MISSIONS.sistemas}
      idPrefix="sistemas-mission"
      doHint={items.length === 0
        ? 'Siguiente acción: registra los sistemas donde viven datos personales (software, nube, proveedores y archivos en papel). Es la base del resto de la ruta.'
        : withoutDpa > 0
          ? `Tienes ${withoutDpa} sistema${withoutDpa === 1 ? '' : 's'} sin contrato de tratamiento (DPA) firmado. Regulariza esos proveedores.`
          : 'Catastro al día. Continúa con el paso 2: Inventario de datos.'}
      evidence={[
        { label: 'Sistemas registrados', value: items.length, tone: items.length ? 'ok' : 'warn' },
        { label: 'Con DPA firmado', value: `${items.length - withoutDpa} de ${items.length}`, tone: withoutDpa ? 'warn' : 'ok' },
        { label: 'Con registro de accesos', value: `${items.length - withoutLogs} de ${items.length}`, tone: withoutLogs ? 'warn' : 'ok' },
      ]}
      evidenceNote="El catastro se incluye en tu Carpeta de evidencias y respalda tu inventario de datos."
    >
    <div className="space-y-6" data-testid="systems-page">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E5E7EB] pb-5">
        <div>
          <div className="text-[15px] font-medium text-[#1C2329]">
            {items.length} sistema{items.length === 1 ? '' : 's'} en el catastro
          </div>
          <div className="text-[13px] text-[#6B7280]">
            Inventario de plataformas, procesos físicos y proveedores que tratan datos personales.
          </div>
        </div>
        <button className="pv-btn-primary flex items-center gap-2" onClick={() => setEditing({ ...EMPTY })} data-testid="system-new">
          <Plus size={14} /> Nuevo sistema
        </button>
      </div>

      {items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <StatCard label="Sistemas registrados" value={items.length} tone="neutral" />
          <StatCard label="Sin DPA firmado" value={withoutDpa} tone={withoutDpa ? 'warn' : 'ok'}
            hint={withoutDpa ? 'Regularice contrato de encargo' : 'Todos con DPA'} />
          <StatCard label="Sin logs automáticos" value={withoutLogs} tone={withoutLogs ? 'info' : 'ok'}
            hint={withoutLogs ? 'Requieren bitácora manual' : 'Todos con logs propios'} />
        </div>
      )}

      {items.length === 0 ? (
        <div className="border border-[#E5E7EB] bg-white p-10 text-center" data-testid="systems-empty">
          <Server size={26} className="mx-auto text-[#9CA3AF]" strokeWidth={1.5} />
          <div className="mt-3 text-lg font-semibold text-[#1C2329]">Aún no hay sistemas registrados</div>
          <div className="text-sm text-[#6B7280] mt-1 max-w-md mx-auto">
            Registre cada plataforma (CRM, ERP, RRHH), proceso físico o proveedor que trata datos personales.
            Cada actividad del RAT podrá asociarse a uno o más sistemas.
          </div>
        </div>
      ) : (
        <div className="border border-[#E5E7EB] bg-white overflow-x-auto">
          <table className="pv-table" data-testid="systems-table">
            <thead><tr>
              <th>Sistema</th><th>Tipo</th><th>Responsable</th>
              <th>Logs</th><th>DPA</th><th style={{ width: 90 }}></th>
            </tr></thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} data-testid={`system-row-${s.id}`}>
                  <td>
                    <button onClick={() => setEditing(s)} className="text-left">
                      <div className="font-medium text-[#1C2329] hover:text-[#143A34]">{s.name}</div>
                      <div className="text-[12px] text-[#6B7280] mt-0.5">
                        {s.provider ? `${s.provider} · ` : ''}{s.hosting_country || 'sin país'}
                      </div>
                    </button>
                  </td>
                  <td className="text-[13px]">{meta.kinds?.[s.kind] || s.kind}</td>
                  <td className="text-[13px]">{s.internal_owner || '—'}</td>
                  <td>
                    <YesNoChip yes={s.has_logs} labelYes="Sí" labelNo="Manual" />
                  </td>
                  <td>
                    {s.dpa_signed ? (
                      <span className="inline-flex items-center gap-1 text-[11.5px] text-[#065F46] bg-[#ECFDF5] border border-[#A7F3D0] px-2 py-0.5">
                        <ShieldCheck size={11} /> Firmado
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11.5px] text-[#92400E] bg-[#FFFBEB] border border-[#FDE68A] px-2 py-0.5">
                        <ShieldOff size={11} /> Pendiente
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setEditing(s)} className="text-[#6B7280] hover:text-[#1C2329] hover:bg-[#F3F4F6] p-1 rounded" data-testid={`system-edit-${s.id}`}>
                        <Pencil size={13} />
                      </button>
                      <button onClick={() => del(s.id)} className="text-[#DC2626] hover:bg-[#FEF2F2] p-1 rounded" data-testid={`system-delete-${s.id}`}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <SystemEditor item={editing} meta={meta} onSave={save} onCancel={() => setEditing(null)} />
      )}
    </div>
    </MissionShell>
  );
}

function StatCard({ label, value, tone, hint }) {
  const toneCls = {
    ok:      'border-[#A7F3D0] bg-[#ECFDF5] text-[#065F46]',
    warn:    'border-[#FDE68A] bg-[#FFFBEB] text-[#92400E]',
    info:    'border-[#BFDBFE] bg-[#EFF6FF] text-[#1E40AF]',
    neutral: 'border-[#E5E7EB] bg-white text-[#1C2329]',
  }[tone] || 'border-[#E5E7EB] bg-white text-[#1C2329]';
  return (
    <div className={`border p-3.5 ${toneCls}`}>
      <div className="text-[11px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-[22px] font-semibold mt-0.5 tabular-nums">{value}</div>
      {hint && <div className="text-[11.5px] opacity-80 mt-0.5">{hint}</div>}
    </div>
  );
}

function YesNoChip({ yes, labelYes, labelNo }) {
  return yes ? (
    <span className="inline-flex items-center gap-1 text-[11.5px] text-[#065F46] bg-[#ECFDF5] border border-[#A7F3D0] px-2 py-0.5">
      <CheckCircle2 size={11} /> {labelYes}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[11.5px] text-[#6B7280] bg-[#F3F4F6] border border-[#E5E7EB] px-2 py-0.5">
      <AlertCircle size={11} /> {labelNo}
    </span>
  );
}

function SystemEditor({ item, meta, onSave, onCancel }) {
  const [form, setForm] = useState({ ...EMPTY, ...item });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? (v.target.type === 'checkbox' ? v.target.checked : v.target.value) : v }));
  const isEdit = !!item?.id;

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white border border-[#E5E7EB] max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="system-editor">
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <div>
            <div className="text-[15px] font-semibold text-[#1C2329]">
              {isEdit ? 'Editar sistema' : 'Nuevo sistema en el catastro'}
            </div>
            <div className="text-[12px] text-[#6B7280] mt-0.5">
              Registra cada plataforma o proceso que trate datos personales.
            </div>
          </div>
          <button onClick={onCancel} className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#6B7280]"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <F label="Nombre del sistema">
            <input className="pv-input" value={form.name} onChange={set('name')} data-testid="sys-name"
              placeholder="Ej: HubSpot, Buk, Carpeta física de RRHH" />
          </F>
          <div className="grid grid-cols-2 gap-4">
            <F label="Proveedor">
              <input className="pv-input" value={form.provider} onChange={set('provider')} placeholder="Ej: HubSpot Inc." />
            </F>
            <F label="Tipo">
              <select className="pv-input" value={form.kind} onChange={set('kind')} data-testid="sys-kind">
                {Object.entries(meta.kinds || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </F>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <F label="País de hosting / ubicación">
              <input className="pv-input" value={form.hosting_country} onChange={set('hosting_country')} placeholder="Ej: Chile, EE.UU." />
            </F>
            <F label="Responsable interno">
              <input className="pv-input" value={form.internal_owner} onChange={set('internal_owner')} placeholder="Ej: Jefe de TI" />
            </F>
          </div>
          <F label="Correo de contacto">
            <input className="pv-input" value={form.contact_email} onChange={set('contact_email')} placeholder="responsable@empresa.cl" />
          </F>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            <label className="flex items-start gap-2 p-3 border border-[#E5E7EB] cursor-pointer hover:border-[#C7D2CE]">
              <input type="checkbox" className="mt-0.5 w-4 h-4 accent-[#143A34]" checked={form.has_logs} onChange={set('has_logs')} data-testid="sys-has-logs" />
              <div>
                <div className="text-[13px] font-medium text-[#1C2329]">¿Genera logs automáticos?</div>
                <div className="text-[11.5px] text-[#6B7280] mt-0.5">Si no, sus eventos deberán registrarse manualmente en la trazabilidad.</div>
              </div>
            </label>
            <label className="flex items-start gap-2 p-3 border border-[#E5E7EB] cursor-pointer hover:border-[#C7D2CE]">
              <input type="checkbox" className="mt-0.5 w-4 h-4 accent-[#143A34]" checked={form.dpa_signed} onChange={set('dpa_signed')} data-testid="sys-dpa" />
              <div>
                <div className="text-[13px] font-medium text-[#1C2329]">¿Tiene DPA firmado?</div>
                <div className="text-[11.5px] text-[#6B7280] mt-0.5">Contrato de encargo con el proveedor (art. 15 bis Ley 21.719).</div>
              </div>
            </label>
          </div>

          {form.dpa_signed && (
            <F label="Fecha de firma del DPA">
              <input type="date" className="pv-input max-w-xs" value={form.dpa_date || ''} onChange={set('dpa_date')} />
            </F>
          )}

          <F label="Notas">
            <textarea className="pv-input" rows={2} value={form.notes || ''} onChange={set('notes')}
              placeholder="Cualquier detalle adicional sobre el sistema o su uso." />
          </F>
        </div>

        <div className="px-6 py-4 border-t border-[#E5E7EB] flex justify-between bg-white">
          <button className="pv-btn-secondary" onClick={onCancel}>Cancelar</button>
          <button className="pv-btn-primary" disabled={!form.name.trim()}
            onClick={() => onSave(form, form.id)} data-testid="sys-save">
            {isEdit ? 'Guardar cambios' : 'Registrar sistema'}
          </button>
        </div>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return <div><label className="block text-[12px] font-medium text-[#374151] mb-1.5">{label}</label>{children}</div>;
}
